import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Beaker, Save, Trash2, Plus, ChevronLeft, Loader2 } from "lucide-react";

const DOSAGE_FORMS = ["Injection", "Oral solution", "Capsule", "Cream", "Ointment", "Suppository", "Sublingual troche", "Nasal spray", "Ophthalmic drops"];
const ROUTES = ["Subcutaneous", "Oral", "Topical", "Rectal", "Sublingual", "Intranasal", "Ophthalmic", "Intramuscular"];
const UNITS = ["mg", "mL", "g", "mcg", "IU", "drops", "units"];
const BUD_RULES = ["USP <795> Aqueous", "USP <795> Non-aqueous", "USP <797> CSP Category 1", "USP <797> CSP Category 2", "Manufacturer PI"];

export default function FormulaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/formulas/${id}`);
        if (!cancelled) setF(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load formula");
        navigate("/formulas");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setIng = (idx, k, v) => setF((prev) => {
    const ing = [...(prev.ingredients || [])];
    ing[idx] = { ...ing[idx], [k]: v };
    return { ...prev, ingredients: ing };
  });
  const addIng = () => setF((prev) => ({
    ...prev,
    ingredients: [...(prev.ingredients || []), { name: "", quantity: 0, unit: "mg" }],
  }));
  const removeIng = (idx) => setF((prev) => ({
    ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx),
  }));

  const setStep = (idx, v) => setF((prev) => {
    const steps = [...(prev.instructions || [])];
    steps[idx] = v;
    return { ...prev, instructions: steps };
  });
  const addStep = () => setF((prev) => ({ ...prev, instructions: [...(prev.instructions || []), ""] }));
  const removeStep = (idx) => setF((prev) => ({ ...prev, instructions: prev.instructions.filter((_, i) => i !== idx) }));

  const setEq = (idx, v) => setF((prev) => {
    const eq = [...(prev.equipment || [])];
    eq[idx] = v;
    return { ...prev, equipment: eq };
  });
  const addEq = () => setF((prev) => ({ ...prev, equipment: [...(prev.equipment || []), ""] }));
  const removeEq = (idx) => setF((prev) => ({ ...prev, equipment: prev.equipment.filter((_, i) => i !== idx) }));

  const setVial = (idx, k, v) => setF((prev) => {
    const arr = [...(prev.vial_specs || [])];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...prev, vial_specs: arr };
  });
  const addVial = (type) => setF((prev) => ({
    ...prev,
    vial_specs: [...(prev.vial_specs || []), { type: type || "", size: "" }],
  }));
  const removeVial = (idx) => setF((prev) => ({ ...prev, vial_specs: prev.vial_specs.filter((_, i) => i !== idx) }));

  const setDisinf = (idx, v) => setF((prev) => {
    const arr = [...(prev.disinfection_agents || [])];
    arr[idx] = v;
    return { ...prev, disinfection_agents: arr };
  });
  const addDisinf = () => setF((prev) => ({ ...prev, disinfection_agents: [...(prev.disinfection_agents || []), ""] }));
  const removeDisinf = (idx) => setF((prev) => ({ ...prev, disinfection_agents: prev.disinfection_agents.filter((_, i) => i !== idx) }));

  const setPh = (k, v) => setF((prev) => ({ ...prev, ph_target: { ...(prev.ph_target || {}), [k]: Number(v) || 0 } }));
  const setFilter = (k, v) => setF((prev) => ({ ...prev, filter_info: { ...(prev.filter_info || {}), [k]: Number(v) || 0 } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/formulas/${id}`, f);
      setF(data);
      toast.success("Formula saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!f) return null;

  const readonly = !isAdmin;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link to="/formulas" className="text-xs text-slate-500 hover:text-pink-700 inline-flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> Back to Master Formulas
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <Beaker className="w-6 h-6 text-pink-700" /> {f.name || "Untitled formula"}
          </h1>
          <p className="text-xs text-slate-500 mt-1">Created {(f.created_at || "").slice(0, 10)} · updated {(f.updated_at || "").slice(0, 10)}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            <Button data-testid="formula-save" onClick={save} disabled={saving}
              className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </Button>
            <Button data-testid="formula-delete" variant="ghost"
              className="gap-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-800"
              onClick={async () => {
                if (!confirm(`PERMANENTLY DELETE "${f.name}"?\n\nThis cannot be undone. Batch logs already spawned from it stay intact (they hold a snapshot).`)) return;
                const c = prompt(`Type DELETE to permanently remove "${f.name}":`);
                if (c !== "DELETE") return toast.info("Cancelled");
                try {
                  await api.delete(`/formulas/${id}`, { params: { hard: true } });
                  toast.success(`"${f.name}" permanently deleted`);
                  navigate("/formulas");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Delete failed");
                }
              }}
              title="Permanently delete (irreversible)">
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          </div>
        )}
      </div>

      <Card className="p-5 mb-5 border-slate-200 space-y-3">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Recipe header</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Name</label>
            <Input data-testid="f-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)}
              disabled={readonly} className="h-9 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Dosage form</label>
            <Input data-testid="f-dosage-form" list="dosage-forms" value={f.dosage_form || ""}
              onChange={(e) => set("dosage_form", e.target.value)} disabled={readonly} className="h-9 text-sm" />
            <datalist id="dosage-forms">{DOSAGE_FORMS.map((x) => <option key={x} value={x} />)}</datalist>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Description</label>
            <textarea data-testid="f-description" value={f.description || ""} rows={2}
              onChange={(e) => set("description", e.target.value)} disabled={readonly}
              className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:border-pink-700 focus:outline-none focus:ring-1 focus:ring-pink-700 disabled:bg-slate-50" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Route of administration</label>
            <Input data-testid="f-route" list="routes" value={f.route_of_administration || ""}
              onChange={(e) => set("route_of_administration", e.target.value)} disabled={readonly} className="h-9 text-sm" />
            <datalist id="routes">{ROUTES.map((x) => <option key={x} value={x} />)}</datalist>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Total qty + unit</label>
            <div className="flex gap-1">
              <Input data-testid="f-total-qty" type="number" step="any" value={f.total_quantity || ""}
                onChange={(e) => set("total_quantity", Number(e.target.value))} disabled={readonly}
                className="h-9 text-sm font-mono w-24" />
              <Input data-testid="f-total-qty-unit" list="units" value={f.total_quantity_unit || "mL"}
                onChange={(e) => set("total_quantity_unit", e.target.value)} disabled={readonly}
                className="h-9 text-sm w-20" />
              <datalist id="units">{UNITS.map((x) => <option key={x} value={x} />)}</datalist>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">BUD rule</label>
            <Input data-testid="f-bud-rule" list="bud-rules" value={f.bud_rule || ""}
              onChange={(e) => set("bud_rule", e.target.value)} disabled={readonly} className="h-9 text-sm" />
            <datalist id="bud-rules">{BUD_RULES.map((x) => <option key={x} value={x} />)}</datalist>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">BUD days</label>
            <Input data-testid="f-bud-days" type="number" value={f.bud_days || 0}
              onChange={(e) => set("bud_days", Number(e.target.value))} disabled={readonly}
              className="h-9 text-sm font-mono" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Storage conditions</label>
            <Input data-testid="f-storage" value={f.storage_conditions || ""}
              onChange={(e) => set("storage_conditions", e.target.value)} disabled={readonly}
              className="h-9 text-sm" placeholder="e.g. Refrigerated 2–8 °C, protect from light" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Visual inspection count</label>
            <Input data-testid="f-vi-count" type="number" value={f.visual_inspection_count || 0}
              onChange={(e) => set("visual_inspection_count", Number(e.target.value))} disabled={readonly}
              className="h-9 text-sm font-mono w-24" />
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Ingredients</h3>
          {isAdmin && <Button size="sm" variant="outline" onClick={addIng} data-testid="f-ing-add" className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add ingredient</Button>}
        </div>
        {(f.ingredients || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">No ingredients yet.</p>
        ) : (
          <div className="space-y-2">
            {f.ingredients.map((ing, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`f-ing-${i}`}>
                <Input value={ing.name || ""} onChange={(e) => setIng(i, "name", e.target.value)}
                  placeholder="Ingredient name" disabled={readonly}
                  className="h-9 text-sm flex-1" data-testid={`f-ing-name-${i}`} />
                <Input type="number" step="any" value={ing.quantity || 0}
                  onChange={(e) => setIng(i, "quantity", Number(e.target.value))} disabled={readonly}
                  className="h-9 text-sm font-mono w-24" data-testid={`f-ing-qty-${i}`} />
                <Input list="units" value={ing.unit || "mg"}
                  onChange={(e) => setIng(i, "unit", e.target.value)} disabled={readonly}
                  className="h-9 text-sm w-20" data-testid={`f-ing-unit-${i}`} />
                {isAdmin && <button onClick={() => removeIng(i)} data-testid={`f-ing-remove-${i}`}
                  className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Instructions (compounding steps)</h3>
          {isAdmin && <Button size="sm" variant="outline" onClick={addStep} data-testid="f-step-add" className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add step</Button>}
        </div>
        {(f.instructions || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">No steps yet.</p>
        ) : (
          <ol className="space-y-2 list-decimal list-inside">
            {f.instructions.map((s, i) => (
              <li key={i} className="flex items-center gap-2" data-testid={`f-step-${i}`}>
                <span className="text-xs text-slate-500 font-mono w-6">{i + 1}.</span>
                <Input value={s} onChange={(e) => setStep(i, e.target.value)} disabled={readonly}
                  className="h-9 text-sm flex-1" data-testid={`f-step-input-${i}`} />
                {isAdmin && <button onClick={() => removeStep(i)} data-testid={`f-step-remove-${i}`}
                  className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Equipment required</h3>
          {isAdmin && <Button size="sm" variant="outline" onClick={addEq} data-testid="f-eq-add" className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add equipment</Button>}
        </div>
        {(f.equipment || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">No equipment listed.</p>
        ) : (
          <div className="space-y-2">
            {f.equipment.map((e, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`f-eq-${i}`}>
                <Input value={e} onChange={(ev) => setEq(i, ev.target.value)} disabled={readonly}
                  className="h-9 text-sm flex-1" data-testid={`f-eq-input-${i}`} />
                {isAdmin && <button onClick={() => removeEq(i)} data-testid={`f-eq-remove-${i}`}
                  className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Vials, Stoppers &amp; Flip Caps</h3>
            <p className="text-[11px] text-slate-500">The recipe defines type + size. Per-batch lot &amp; expiry are captured when compounding.</p>
          </div>
          {isAdmin && (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => addVial("Amber Vial")} data-testid="f-vial-add-vial" className="h-7 gap-1 text-xs">
                <Plus className="w-3 h-3" /> Vial
              </Button>
              <Button size="sm" variant="outline" onClick={() => addVial("Stopper")} data-testid="f-vial-add-stopper" className="h-7 gap-1 text-xs">
                <Plus className="w-3 h-3" /> Stopper
              </Button>
              <Button size="sm" variant="outline" onClick={() => addVial("Flip Cap")} data-testid="f-vial-add-cap" className="h-7 gap-1 text-xs">
                <Plus className="w-3 h-3" /> Cap
              </Button>
            </div>
          )}
        </div>
        {(f.vial_specs || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">No vial/stopper/cap specs yet — add them so batches print the right containers.</p>
        ) : (
          <div className="space-y-2">
            {f.vial_specs.map((v, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`f-vial-${i}`}>
                <Input value={v.type || ""} onChange={(e) => setVial(i, "type", e.target.value)}
                  placeholder="Type (Amber Vial / Stopper / Flip Cap)"
                  disabled={readonly} className="h-9 text-sm flex-1" data-testid={`f-vial-type-${i}`} />
                <Input value={v.size || ""} onChange={(e) => setVial(i, "size", e.target.value)}
                  placeholder="Size (3 mL / 20 mm)"
                  disabled={readonly} className="h-9 text-sm w-40" data-testid={`f-vial-size-${i}`} />
                {isAdmin && <button onClick={() => removeVial(i)} data-testid={`f-vial-remove-${i}`}
                  className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Disinfection &amp; cleaning agents</h3>
            <p className="text-[11px] text-slate-500">Named agents pre-print on the batch worksheet with blank lot/exp lines.</p>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={addDisinf} data-testid="f-disinf-add" className="h-7 gap-1 text-xs">
              <Plus className="w-3 h-3" /> Add agent
            </Button>
          )}
        </div>
        {(f.disinfection_agents || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">No agents yet — defaults to Peridox RTU + Sterile IPA 70% on the printed worksheet.</p>
        ) : (
          <div className="space-y-2">
            {f.disinfection_agents.map((a, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`f-disinf-${i}`}>
                <Input value={a} onChange={(e) => setDisinf(i, e.target.value)}
                  placeholder="Agent name (e.g. Peridox RTU)"
                  disabled={readonly} className="h-9 text-sm flex-1" data-testid={`f-disinf-input-${i}`} />
                {isAdmin && <button onClick={() => removeDisinf(i)} data-testid={`f-disinf-remove-${i}`}
                  className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 mb-5 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">pH target &amp; sterile filter</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">pH min</label>
            <Input type="number" step="any" value={(f.ph_target || {}).min || 0}
              onChange={(e) => setPh("min", e.target.value)} disabled={readonly}
              className="h-9 text-sm font-mono" data-testid="f-ph-min" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">pH max</label>
            <Input type="number" step="any" value={(f.ph_target || {}).max || 0}
              onChange={(e) => setPh("max", e.target.value)} disabled={readonly}
              className="h-9 text-sm font-mono" data-testid="f-ph-max" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Filter micron</label>
            <Input type="number" step="any" value={(f.filter_info || {}).micron_size || 0}
              onChange={(e) => setFilter("micron_size", e.target.value)} disabled={readonly}
              className="h-9 text-sm font-mono" placeholder="0.22"
              data-testid="f-filter-micron" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Bubble pt (psi)</label>
            <Input type="number" step="any" value={(f.filter_info || {}).manufacturer_bubble_point_psi || 0}
              onChange={(e) => setFilter("manufacturer_bubble_point_psi", e.target.value)} disabled={readonly}
              className="h-9 text-sm font-mono" placeholder="50"
              data-testid="f-filter-bp" />
          </div>
          <div className="md:col-span-4">
            <label className="text-[10px] text-slate-500 uppercase">CSP category (BUD storage summary)</label>
            <Input value={f.csp_category || ""} onChange={(e) => set("csp_category", e.target.value)}
              disabled={readonly} className="h-9 text-sm"
              placeholder="e.g. USP 797 Category 3 — 90 days refrigerated"
              data-testid="f-csp-category" />
          </div>
          <div className="md:col-span-4 border-t border-slate-100 pt-3 mt-1">
            <label className="text-[10px] text-slate-500 uppercase">
              Printed worksheet theme color · header line, section bars, ingredient/step row headings
            </label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color" data-testid="f-print-color"
                value={f.print_color_hex || "#be185d"}
                onChange={(e) => set("print_color_hex", e.target.value.toLowerCase())}
                disabled={readonly}
                className="h-9 w-16 rounded-md border border-slate-200 cursor-pointer disabled:opacity-60" />
              <Input value={f.print_color_hex || ""}
                onChange={(e) => set("print_color_hex", e.target.value.toLowerCase())}
                disabled={readonly} placeholder="#be185d (default Vera red)"
                className="h-9 text-sm font-mono w-40"
                data-testid="f-print-color-hex" />
              <span className="text-[11px] text-slate-500">
                Leave blank to inherit the default Vera pink. Applies only to this recipe's printed batch worksheets.
              </span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
